import { T, useTranslate } from '@tolgee/react';
import { StaticImageData } from 'next/image';
import Link from 'next/link';
import React, { useState } from 'react';
import { useIntercom } from 'react-use-intercom';
import styled from 'styled-components';

import EnterprisePlanIcon from '../../assets/icons/EnterprisePlanIcon';
import Logo from '../../assets/icons/Logo';
import texasStaarImage from '../../assets/images/texastestprepscrecr.jpg';
import texasTelpasImage from '../../assets/images/texastestpreptelpas.png';
import theme from '../../assets/theme';
import useAccessBadge from '../../hooks/useAccessBadge';
import { useMetrics } from '../../hooks/useMetrics';
import californiaCaaspImage from '../../public/California-CAASP-ELA.jpg';
import californiaElpacImage from '../../public/California-ELPAC.png';
import { Button } from '../_atoms/Button';
import SupportedTestPrepCard from '../_molecules/SupportedTestPrepCard';

// Types
export interface TestPrepCard {
  image: StaticImageData;
  alt: string;
  firstSectionTitle: string;
  firstSectionItems: string[];
  secondSectionTitle: string;
  secondSectionItems: string[];
}

export interface StateSpecificTestPrepPaywallProps {
  variant: 'TX' | 'CA';
  titleKey: string;
  titleDefault: string;
  subtitleKey: string;
  subtitleDefault: string;
  caseStudyUrl: string;
  featureItems: string[];
  testPrepCards: TestPrepCard[];
  chatMessage: string;
}

// Styled Components
const Container = styled.div`
  text-align: center;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const LogoWrapper = styled.div`
  color: ${theme.colors.orange};
  width: 150px;
  height: 42px;
  display: flex;
  align-items: center;
`;

const SchoolPlanBadge = styled.span`
  background: ${theme.colors.orange};
  color: white;
  padding: 4px 12px;
  border-radius: 4px;
  font-weight: bold;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Title = styled.h1`
  font-size: 32px !important;
  margin-bottom: 30px;
  line-height: 1.2;
  font-weight: 800 !important;
`;

const Subtitle = styled.p`
  font-size: 18px;
  margin-bottom: 30px;
`;

const CaseStudyLink = styled.a`
  color: ${theme.colors.black};
  cursor: pointer;
  text-decoration: underline;
  transition: all 0.2s ease;

  &:hover {
    color: ${theme.colors.orange};
    text-decoration: none;
  }
`;

const WhiteFeatureContainer = styled.div`
  background: white;
  padding: 20px;
  border-radius: 15px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.08);
  margin: 0 auto 30px;
  max-width: 400px;
`;

const FeatureListInner = styled.div`
  text-align: left;
  display: flex;
  flex-direction: column;
  font-weight: 700;
`;

const FeatureItem = styled.p`
  font-size: 18px;
  margin: 0;
`;

const DetailsToggle = styled.p`
  font-size: 16px;
  margin-bottom: 30px;
  color: ${theme.colors.greyDark};
  cursor: pointer;
  text-decoration: underline;
`;

const TestPrepCardsContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 40px;
  flex-wrap: wrap;
  justify-content: center;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 15px;

  @media (max-width: 480px) {
    width: 100%;
    gap: 10px;

    & > button {
      width: 100%;
      max-width: 250px;
    }
  }
`;

const ButtonsInner = styled.div`
  display: flex;
  gap: 15px;
  flex-wrap: wrap;
  justify-content: center;
`;

// Main Component
export const StateSpecificTestPrepPaywall: React.FC<StateSpecificTestPrepPaywallProps> = ({
  variant,
  titleKey,
  titleDefault,
  subtitleKey,
  subtitleDefault,
  caseStudyUrl,
  featureItems,
  testPrepCards,
  chatMessage,
}) => {
  const { t } = useTranslate();
  const { showNewMessages } = useIntercom();
  const { accessBadge } = useAccessBadge();
  const [showDetails, setShowDetails] = useState(false);
  const metrics = useMetrics();

  const canPurchaseIndividual = accessBadge?.canPurchaseIndividualPremium ?? false;

  return (
    <Container>
      <Header>
        <LogoWrapper>
          <Logo style={{ width: '100%', height: '100%' }} />
        </LogoWrapper>
        {!canPurchaseIndividual && (
          <SchoolPlanBadge>
            <EnterprisePlanIcon width="20px" height="20px" style={{ display: 'block' }} />
            <span style={{ display: 'block' }}>{t('SCHOOL PLAN')}</span>
          </SchoolPlanBadge>
        )}
      </Header>

      <Title>
        <T keyName={titleKey} defaultValue={titleDefault} />
      </Title>

      <Subtitle>
        {t(subtitleKey, subtitleDefault)}{' '}
        <CaseStudyLink href={caseStudyUrl} target="_blank" rel="noopener noreferrer">
          <T keyName="Read more here" defaultValue="Read more here" />
        </CaseStudyLink>
        .
      </Subtitle>

      <WhiteFeatureContainer>
        <FeatureListInner>
          {featureItems.map((item, index) => (
            <FeatureItem key={index}>✓ {item}</FeatureItem>
          ))}
        </FeatureListInner>
      </WhiteFeatureContainer>

      <DetailsToggle
        onClick={() => {
          if (!showDetails) {
            metrics.logEvent('StateSpecificTestPrepPaywall.ShowDetails', { variant });
          }
          setShowDetails(!showDetails);
        }}
      >
        {showDetails ? '- Hide detailed breakdown' : '+ See all supported question types'}
      </DetailsToggle>

      {showDetails && (
        <TestPrepCardsContainer>
          {testPrepCards.map((card, index) => (
            <SupportedTestPrepCard
              key={index}
              image={card.image}
              alt={card.alt}
              firstSectionTitle={card.firstSectionTitle}
              firstSectionItems={card.firstSectionItems}
              secondSectionTitle={card.secondSectionTitle}
              secondSectionItems={card.secondSectionItems}
            />
          ))}
        </TestPrepCardsContainer>
      )}

      <ButtonContainer>
        <ButtonsInner>
          <Button
            type="ghost"
            onClick={() => {
              metrics.logEvent('SchoolPlanPaywall.ChatWithUs', { variant });
              showNewMessages(
                canPurchaseIndividual ? 'Help me unlock unlimited access.' : chatMessage,
              );
            }}
          >
            <T keyName="Chat with us" defaultValue="Chat with us" />
          </Button>
          <Link
            href={
              canPurchaseIndividual
                ? '/pricing'
                : 'https://calendly.com/d/cxbw-rg3-8y3/curipod-quote-request'
            }
            passHref
          >
            <a
              target={canPurchaseIndividual ? '_self' : '_blank'}
              rel="noopener noreferrer"
            >
              <Button
                type="primary"
                onClick={() => {
                  metrics.logEvent(
                    canPurchaseIndividual ? 'Paywall.SeePricing' : 'Paywall.RequestQuote',
                    { variant },
                  );
                }}
                styles={{
                  backgroundColor: theme.colors.orange,
                  borderColor: theme.colors.orange,
                }}
              >
                {canPurchaseIndividual
                  ? t('SchoolPlanPaywall.SeePricing', 'See pricing')
                  : t('Get a quote!', 'Get a quote!')}
              </Button>
            </a>
          </Link>
        </ButtonsInner>
      </ButtonContainer>
    </Container>
  );
};

// Texas Wrapper Component
export const TexasSchoolPlanPaywall = () => {
  return (
    <StateSpecificTestPrepPaywall
      variant="TX"
      titleKey="TexasPaywall.Title"
      titleDefault="You've Generated 3/3 of your Free Test Prep Lessons: Upgrade to the School Plan for Full Access to STAAR & TELPAS."
      subtitleKey="TexasPaywall.Subtitle"
      subtitleDefault="Curipod’s test prep is proven to increase ‘meets’ and ‘masters’ by double digits."
      caseStudyUrl="/case-studies/curipod-case-study-green-valley"
      featureItems={[
        'STAAR: Grades 3-8 + HS EOC',
        'TELPAS: K-12, all proficiency levels',
        'RLA, Math, Social Studies, & Science',
      ]}
      testPrepCards={[
        {
          image: texasStaarImage,
          alt: 'STAAR Test Prep',
          firstSectionTitle: 'Supported Grade Levels & Subjects:',
          firstSectionItems: [
            'RLA: 3rd-8th + HS EOC English I & II',
            'Math: 3rd-8th + HS EOC Algebra',
            'Science: 5th, 8th + EOC Biology',
            'Social Studies: 8th + EOC U.S. History',
          ],
          secondSectionTitle: 'Supported Question Types:',
          secondSectionItems: [
            'SCR',
            'Paired Passage',
            'ECR: Informational, Argumentative, Correspondence',
          ],
        },
        {
          image: texasTelpasImage,
          alt: 'TELPAS Test Prep',
          firstSectionTitle: 'Supported Grade & Proficiency Levels:',
          firstSectionItems: [
            'Grades: K-12',
            'Proficiency Levels: Beginning, Intermediate, Advanced, & Advanced High',
          ],
          secondSectionTitle: 'Supported Question Types:',
          secondSectionItems: [
            'Tell a Story: Answer Questions',
            'Tell a Story: One Picture',
            'Tell a Story: Four Pictures',
            'Describe the Pictures',
            'Respond to a Scenario',
            'Write About a Bar Graph',
          ],
        },
      ]}
      chatMessage="Hi! I would like help getting access to STAAR/TELPAS test prep."
    />
  );
};

// California Wrapper Component
export const CaliforniaSchoolPlanPaywall = () => {
  return (
    <StateSpecificTestPrepPaywall
      variant="CA"
      titleKey="CaliforniaPaywall.Title"
      titleDefault="You've Generated 3/3 of your Free Test Prep Lessons: Upgrade to the School Plan for Full Access to CAASPP & ELPAC."
      subtitleKey="CaliforniaPaywall.Subtitle"
      subtitleDefault="Curipod's test prep is proven to increase student achievement by double digits."
      caseStudyUrl="https://curipod.com/Curipod_Case_Study_California.pdf"
      featureItems={[
        'CAASPP: Grades 3-12',
        'ELPAC: K-12, all proficiency levels',
        'Core subjects + EOC support',
      ]}
      testPrepCards={[
        {
          image: californiaCaaspImage,
          alt: 'CAASPP Test Prep',
          firstSectionTitle: 'Supported Grade Levels & Subjects:',
          firstSectionItems: ['ELA: 3rd-12th', 'Math: 3rd-8th'],
          secondSectionTitle: 'Supported Question Types:',
          secondSectionItems: [
            'Short Answer',
            'Paired Passage Short Answer',
            'Performance Task: Informational, Opinion, Argumentative, Narrative',
            'Math Constructed Response: 1-4 Point',
          ],
        },
        {
          image: californiaElpacImage,
          alt: 'ELPAC Test Prep',
          firstSectionTitle: 'Supported Grade & Proficiency Levels:',
          firstSectionItems: ['Grades: K-12', 'All proficiency levels'],
          secondSectionTitle: 'Supported Question Types:',
          secondSectionItems: ['Write About an Experience'],
        },
      ]}
      chatMessage="Hi! I would like help getting access to CAASPP/ELPAC test prep."
    />
  );
};
